/**
 * Premium (D2C) — user subscription to the premium content catalog.
 * Subscribe has NO redirect: requires a saved `mitEligible` card,
 * otherwise 400 → UI leads to wallet setup.
 *
 * BE returns RAW responses (no `{ data }` wrapper).
 */
import type { BillingInterval, Currency } from '@mfw/common';

import type {
  ApiPremiumSubscriptionStatus,
  SubscribePremiumArgs,
  ApiSubscription,
  ApiPremiumPlan,
} from './types/payments.types';

import { post, get } from './axios';

// currency filter (R-AUD-53): plans exist in both currencies — only one is shown
export const fetchPremiumPlans = async (currency?: Currency) => {
  const response = await get<ApiPremiumPlan[]>('/v2/payments/premium/plans', {
    query: currency ? { currency } : undefined,
  });
  return response ?? [];
};

export const fetchPremiumSubscription = async () => {
  const response = await get<ApiPremiumSubscriptionStatus>(
    '/v2/payments/premium/subscription',
  );
  return response ?? null;
};

export const subscribePremium = async ({ planId }: SubscribePremiumArgs) => {
  const response = await post('/v2/payments/premium/subscribe', { planId });
  // `post` helper types the `{ data }` wrapper, but BE returns a raw body here — re-typing.
  return (response ?? null) as ApiSubscription | null;
};

export const cancelPremium = async () => {
  const response = await post('/v2/payments/premium/cancel');
  return (response ?? null) as ApiSubscription | null;
};

export const resumePremium = async () => {
  const response = await post('/v2/payments/premium/resume');
  return (response ?? null) as ApiSubscription | null;
};

export const payNowPremium = async () => {
  const response = await post('/v2/payments/premium/pay-now');
  return (response ?? null) as { scheduled: boolean } | null;
};

/**
 * Q8: schedule the monthly ↔ yearly switch. Nothing is charged now — the
 * switch takes effect at the period end. Sending the interval the subscription
 * is ALREADY on withdraws a scheduled switch; with nothing scheduled the BE
 * answers 400 `PREMIUM_INTERVAL_UNCHANGED`.
 *
 * The answer is the full subscription status, so the caller re-renders from it.
 */
export const changePremiumInterval = async (interval: BillingInterval) => {
  const response = await post('/v2/payments/premium/change-interval', {
    interval,
  });
  return (response ?? null) as ApiPremiumSubscriptionStatus | null;
};

// ——— premium_report = "MyFitWorld Platform Analytics" (S-ODL-67; Task 7.1):
// paid by the ORGANIZATION — fixed at €9.99/mo, charged by the billing runner
// to the organization's saved card, no redirect. Every route is under the
// organization (`organizations/:organizationId/premium-report/*`). ———

export type ApiPremiumReportStatus = {
  // Task 4.5 — the launch switch: a platform user always true (P3); a
  // regular user only once PLATFORM_ANALYTICS_ENABLED is on. The sidebar
  // row, the page and the card all read this single field.
  available: boolean;
  // V2-28/6 — does the ORGANIZATION have Platform Analytics (the BE's one
  // entitlement place; Task 7.2 adds the free grant there). Render this —
  // never re-derive it from `subscription.status`.
  hasAccess: boolean;
  // Task 7.2 — the last free day (civil 'yyyy-MM-dd', inclusive) of a running
  // "Free analytics" grant (a platform user's 100 % discount), else null.
  freeUntil: string | null;
  // V2-29/5 — the subscription's next charge falls inside the grant, so it is
  // charged 0 (the BE's own pricing rule, never re-derived here)
  nextChargeFree: boolean;
  subscription: {
    currentPeriodStart: string | null;
    currentPeriodEnd: null | string;
    nextChargeAt: string | null;
    cancelAtPeriodEnd: boolean;
    unitAmountMinor: string;
    interval: string;
    currency: string;
    status: string;
    id: string;
  } | null;
};

const premiumReportPath = (organizationId: string, action: string) =>
  `/v2/organizations/${organizationId}/premium-report/${action}`;

export const fetchPremiumReportSubscription = async (
  organizationId: string,
) => {
  const response = await get<ApiPremiumReportStatus>(
    premiumReportPath(organizationId, 'subscription'),
  );
  return response ?? null;
};

/**
 * The organization subscribes. With no usable ORGANIZATION card the BE answers
 * 400 `NO_SAVED_CARD` (platform billing's own refusal) — the UI points to the
 * organization's cards.
 */
export const subscribePremiumReport = async (organizationId: string) => {
  const response = await post(premiumReportPath(organizationId, 'subscribe'));
  // `post` helper types the `{ data }` wrapper, but BE returns a raw body — re-typing.
  return (response ?? null) as ApiSubscription | null;
};

export const cancelPremiumReport = async (organizationId: string) => {
  const response = await post(premiumReportPath(organizationId, 'cancel'));
  return (response ?? null) as ApiSubscription | null;
};

/**
 * Q8: withdraw a pending report cancellation. Only that — an ENDED
 * subscription is refused with `SUBSCRIPTION_ALREADY_ENDED`, which the card
 * turns into "subscribe again". Answers the report status, like the GET.
 */
export const resumePremiumReport = async (organizationId: string) => {
  const response = await post(premiumReportPath(organizationId, 'resume'));
  return (response ?? null) as ApiPremiumReportStatus | null;
};
